import type { ApiResponse } from "@/types";
import type { ApiUserMeResponse } from "@/types/api-auth";
import type { SessionUser, User } from "@/types";

/** Error codes that mean stored credentials are invalid — safe to wipe `oktogo_session`. */
const DEFINITIVE_SESSION_CLEAR_CODES = new Set([
  "UNAUTHORIZED",
  "INVALID_REFRESH_TOKEN",
  "REFRESH_TOKEN_REVOKED",
  "NOT_FOUND",
]);

/** Transient failures during deploy / rolling restart — keep tokens and retry or degrade gracefully. */
const TRANSIENT_RESTORE_CODES = new Set([
  "NETWORK_ERROR",
  "SERVICE_UNAVAILABLE",
  "INVALID_RESPONSE",
  "ABORTED",
  "RATE_LIMIT_EXCEEDED",
]);

export type SessionRestoreSuccess = {
  kind: "ok";
  data: ApiUserMeResponse;
};

export type SessionRestoreDegraded = {
  kind: "degraded";
};

export type SessionRestoreAuthFailed = {
  kind: "auth_failed";
};

export type SessionRestoreResult =
  | SessionRestoreSuccess
  | SessionRestoreDegraded
  | SessionRestoreAuthFailed;

export type SessionRestoreRetryOptions = {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  totalBudgetMs: number;
};

export const DEFAULT_SESSION_RESTORE_RETRY: SessionRestoreRetryOptions = {
  maxAttempts: 12,
  initialDelayMs: 500,
  maxDelayMs: 8_000,
  /** Covers typical rolling deploy / container restart on stage and production. */
  totalBudgetMs: 90_000,
};

export function isDefinitiveSessionRestoreFailure(res: ApiResponse<unknown>): boolean {
  if (res.success) return false;
  const code = res.error?.code ?? "";
  return DEFINITIVE_SESSION_CLEAR_CODES.has(code);
}

export function isTransientSessionRestoreFailure(res: ApiResponse<unknown>): boolean {
  if (res.success) return false;
  const code = res.error?.code ?? "";
  if (TRANSIENT_RESTORE_CODES.has(code)) return true;
  if (/^HTTP_5\d\d$/.test(code)) return true;
  return false;
}

export function shouldClearStoredSessionOnRestoreFailure(res: ApiResponse<unknown>): boolean {
  return isDefinitiveSessionRestoreFailure(res);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Validate the stored session against GET /users/me with exponential backoff.
 * Does not clear browser storage — callers decide based on the result kind.
 */
export async function fetchUsersMeWithRetry(
  fetchMe: () => Promise<ApiResponse<ApiUserMeResponse>>,
  options: SessionRestoreRetryOptions = DEFAULT_SESSION_RESTORE_RETRY,
): Promise<SessionRestoreResult> {
  const startedAt = Date.now();
  let attempt = 0;
  let delayMs = options.initialDelayMs;
  let lastTransient = false;

  while (attempt < options.maxAttempts && Date.now() - startedAt < options.totalBudgetMs) {
    attempt += 1;
    const res = await fetchMe();

    if (res.success && res.data) {
      return { kind: "ok", data: res.data };
    }

    if (shouldClearStoredSessionOnRestoreFailure(res)) {
      return { kind: "auth_failed" };
    }

    if (isTransientSessionRestoreFailure(res)) {
      lastTransient = true;
    } else {
      // Unknown client/API errors (e.g. 403 guardrails) — do not wipe remember-me tokens.
      return { kind: "degraded" };
    }

    const elapsed = Date.now() - startedAt;
    const remainingBudget = options.totalBudgetMs - elapsed;
    if (attempt >= options.maxAttempts || remainingBudget <= 0) {
      break;
    }

    const waitMs = Math.min(delayMs, remainingBudget);
    await sleep(waitMs);
    delayMs = Math.min(delayMs * 2, options.maxDelayMs);
  }

  return lastTransient ? { kind: "degraded" } : { kind: "auth_failed" };
}

const disconnectedConnections = {
  instagram: { status: "not_connected" as const },
  whatsapp: { status: "not_connected" as const },
  bookingPlatform: { status: "not_connected" as const },
};

/** Minimal User for offline / API-unavailable restore from the persisted session snapshot. */
export function userFromSessionSnapshot(sessionUser: SessionUser): User {
  return {
    id: sessionUser.id,
    email: sessionUser.email,
    name: sessionUser.name,
    avatarUrl: sessionUser.avatarUrl,
    emailVerified: sessionUser.emailVerified,
    onboardingComplete: sessionUser.onboardingComplete,
    accountVertical: sessionUser.accountVertical,
    preferredUiLocale: sessionUser.preferredUiLocale ?? null,
    studioRoles: sessionUser.studioRoles,
    studios: sessionUser.studioIds.map((studioId) => ({
      id: studioId,
      name: "",
      onboardingComplete: sessionUser.onboardingComplete,
      connections: disconnectedConnections,
    })),
  };
}

/** Interval for background revalidation after a degraded restore (ms). */
export const SESSION_REVALIDATION_INTERVAL_MS = 5_000;
